// Übergabe-Assistent — geführte Erfassung einer Wohnungsübergabe auf dem Tablet vor
// Ort (Treppenhaus, Keller, oft mit einer Hand bedient). Hält den gemeinsamen
// Zustand (Entwurf, aktueller Schritt, Speicherstatus) und sichert jede Änderung
// entprellt lokal (IndexedDB via lib/uebergabe/lokal.ts) — ein Funkloch oder ein
// versehentliches Neuladen darf keine erfasste Arbeit kosten.
//
// Diese Etappe (Task 8) baute die ersten drei Schritte aus: Rahmen, Rundgang und
// Zähler-Kontrolle. Task 9 ergänzt Schlüssel/Bemerkungen/Unterschriften sowie den
// eigentlichen Abschluss (schliesseAb): Hochladen von Fotos/Unterschriften, Upsert
// des Protokolls, Aufruf der Finalisierungs-Function. Schlägt schliesseAb fehl,
// bleibt der lokale Entwurf bewusst erhalten (loescheEntwurf läuft erst danach, in
// einem eigenen try/catch, das einen erfolgreichen Server-Abschluss niemals als
// Fehlschlag meldet) — sonst wäre die Vor-Ort-Arbeit bei einem Funkloch oder
// Server-Fehler verloren, oder der Mitarbeiter glaubte fälschlich an einen Fehler.
//
// Fix-Runde 1 (Review nach Task 9): entwurf.unterschrift_entwuerfe (siehe
// lib/uebergabe/lokal.ts) trägt jetzt die editierbaren Name/Ort/Datum-Felder der
// Unterschriften-Karten (Schritt 6) — vorher lagen sie in einem lokalen useState von
// SchrittUnterschriften.tsx und gingen bei jedem Schrittwechsel verloren, weil der
// Assistent je Schritt eine andere Komponente an derselben Stelle rendert.
//
// Fix-Runde 2: Notanker-Logik nach lib/uebergabe/lokal.ts verschoben (dort getestet)
// und um Bereinigung fehlender Foto-/Unterschrift-Blobs ergänzt — der Notanker kann
// keine Blobs tragen, sein Protokoll-Stand kann also auf einen Pfad verweisen, dessen
// Blob nur in einer älteren IndexedDB-Kopie existiert (Absturz kurz nach einem
// Mangel-Foto). Fehlende Verweise werden entfernt und als dauerhafter, manuell
// wegklickbarer Hinweis gemeldet — lieber ehrlich als ein Protokoll, das
// Vollständigkeit vortäuscht.
//
// Abschluss-Review: zwei Riegel kamen dazu — (K3) nach erfolgreichem Abschluss schreibt
// weder Debounce-Timer noch Unmount-Flush noch etwas lokal zurück (der Entwurf erstand
// sonst nach JEDER Übergabe wieder auf), und (W2) Fehler beim lokalen Speichern werden
// nicht mehr verschluckt, sondern als bleibender roter Hinweis gezeigt.
//
// Optik: Widget-Stil nach design_handoff_uebergabe (17.10.2026) im globalen Hell/Dunkel — Kopf mit
// Netz-Chip und Balken-Schrittleiste, Hinweise als Widgets, untere Leiste sticky. Rückfragen und
// Meldungen als App-Dialog/Toast (components/ui/AppMeldungen) statt window.confirm/alert.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CaretLeft, CaretRight, Check, CircleNotch, WarningCircle, WifiHigh, WifiSlash, X } from '@phosphor-icons/react';
import { useAppMeldungen } from '../../../components/ui/AppMeldungen';
import { BalkenSchritte } from '../../../components/ui/BalkenSchritte';
import { HinweisWidget } from '../../../components/ui/HinweisWidget';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { useViewport } from '../../../components/island/hooks';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { ART_FARBE, ART_LABEL } from '../../../lib/uebergabeAnsicht/darstellung';
import { ART_ICON } from './liste/ListenTeile';
import { supabase } from '../../../lib/supabase/client';
import {
  ladeEntwurf, loescheEntwurf, speichereEntwurf, erzeugeEntwurf,
  schreibeNotfallSchnappschuss, uebernehmeNotfallSchnappschuss,
  type LokalerEntwurf,
} from '../../../lib/uebergabe/lokal';
import { schliesseAb, echteDeps, abschlussMeldungen } from '../../../lib/uebergabe/abschluss';
import { entscheideBeimOeffnen, echteSyncDeps, holeVomServer, ladeServerStand, schiebeHoch } from '../../../lib/uebergabe/sync';
import { verteileRollen, ladeKontakteFuerBezug, type Bezug } from '../../../lib/uebergabe/bezug';
import { istEndgueltig } from '../../../lib/uebergabe/listenzeilen';
import { holeServerStatus } from '../../../lib/uebergabe/serverstatus';
import SchrittRahmen from './SchrittRahmen';
import SchrittRundgang from './SchrittRundgang';
import SchrittZaehlerKontrolle from './SchrittZaehlerKontrolle';
import SchrittSchluessel from './SchrittSchluessel';
import SchrittBemerkungen from './SchrittBemerkungen';
import SchrittUnterschriften from './SchrittUnterschriften';
import { pruefeAbschlussBereit } from '../../../lib/uebergabe/unterschriften';
import LadeHinweis from './LadeHinweis';

interface UebergabeAssistentProps {
  protokollId: string;
  userProfile?: any;
  // Vorauswahl aus der Pille (UebergabeVorauswahl.tsx über UebergabeListe.tsx) — nur bei
  // echter Neuanlage relevant (s. Ladeeffekt unten): ein bereits vorhandener lokaler
  // Entwurf gewinnt immer dagegen, ein Direktaufruf der Route (Reload, "Weiter
  // bearbeiten") bekommt hier ohnehin null von UebergabeListe.tsx.
  bezug?: Bezug | null;
  onFertig: () => void;
  onAbbrechen: () => void;
  /** Meldungen, die erst NACH onFertig/onAbbrechen erscheinen — der Assistent ist dann schon
   *  ausgehängt, seine eigenen Toasts/Dialoge gingen verloren (früher blieb alert() stehen).
   *  Ohne eigene Meldungsfläche (Hausmeister-App) bleibt es beim Browser-Hinweis wie bisher. */
  meldeDanach?: (text: string, fehler?: boolean) => void;
  /** Hausmeister-App (Portale E3): Schritte antippbar, Art/Objekt/Einheit gesperrt, kein Abstand
   *  für den Chat-Knopf. Ohne die Option bleibt der Assistent im Büro genau wie bisher. */
  fuerHausmeister?: boolean;
}

type Schritt = 1 | 2 | 3 | 4 | 5 | 6;
const SCHRITTE: Schritt[] = [1, 2, 3, 4, 5, 6];
const SCHRITT_LABEL: Record<Schritt, string> = {
  1: 'Rahmen', 2: 'Rundgang', 3: 'Zähler-Kontrolle', 4: 'Schlüssel', 5: 'Bemerkungen', 6: 'Unterschriften',
};
const LETZTER_SCHRITT: Schritt = 6;

type SetEntwurf = (updater: LokalerEntwurf | ((vorher: LokalerEntwurf) => LokalerEntwurf)) => void;

// Notanker-Logik (localStorage-Fallback für den Flush bei pagehide/visibilitychange,
// samt Bereinigung von Foto-/Unterschrift-Pfaden ohne Blob) liegt seit Fix-Runde 2 in
// lib/uebergabe/lokal.ts — dort auch mit Tests. Hier nur noch Verdrahtung: schreiben
// im Flush, übernehmen beim Laden, fehlende Medien als dauerhaften Hinweis anzeigen.

// Neuanlage eines Entwurfs (Art/Objekt/Einheit/Kontakte, Standard-Schlüssel etc.) liegt
// seit Fix-Runde 1 in lib/uebergabe/lokal.ts (erzeugeEntwurf) — sie wird jetzt von ZWEI
// Stellen gebraucht: eager beim Bestätigen der Pillen-Vorauswahl (UebergabeListe.tsx,
// damit ein schneller Reload direkt danach nichts verliert) und hier weiterhin als
// defensiver Fallback, falls die eager-Anlage dort z. B. an blockiertem IndexedDB
// scheiterte. Hier nur noch die kontakte→Parteien-Auflösung (netzwerkabhängig, daher
// nicht Teil der reinen erzeugeEntwurf-Funktion selbst).

const UebergabeAssistent: React.FC<UebergabeAssistentProps> = ({ protokollId, userProfile, bezug, onFertig, onAbbrechen, meldeDanach = (text: string) => window.alert(text), fuerHausmeister = false }) => {
  const [entwurf, setEntwurfState] = useState<LokalerEntwurf | null>(null);
  const viewport = useViewport();
  const { mobil } = useMobilModus();
  const { melde, bestaetige, dialoge } = useAppMeldungen(mobil);
  const [schritt, setSchritt] = useState<Schritt>(1);
  const [speichertGerade, setSpeichertGerade] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [objektKopf, setObjektKopf] = useState<{ bezeichnung: string; adresse: string } | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [ladeFehler, setLadeFehler] = useState(false);
  const [ladeVersuch, setLadeVersuch] = useState(0);
  const [objektKopfFehler, setObjektKopfFehler] = useState(false);
  const [objektKopfVersuch, setObjektKopfVersuch] = useState(0);
  // Bleibt stehen, bis der Mitarbeiter sie aktiv wegklickt (kein Auto-Dismiss beim
  // Schrittwechsel) — ein durch den Notanker verlorenes Beweisfoto darf nicht
  // stillschweigend durchrutschen.
  const [fehlendeMedienHinweis, setFehlendeMedienHinweis] = useState<string[] | null>(null);
  // Fund W2 (Abschluss-Review): Fehler beim lokalen Speichern wurden bislang verschluckt
  // (jedes `void speichereEntwurf(…)` ohne .catch), und die Anzeige „Speichert…"
  // verschwand wie im Erfolgsfall. Der IndexedDB-Wrapper meldet einen
  // Transaktionsabbruch (z. B. voller Speicher) eigens — nur erfuhr das niemand, und der
  // Mitarbeiter arbeitete vor Ort im guten Glauben weiter, während nichts mehr gesichert
  // wurde. Der Hinweis bleibt deshalb stehen, bis er aktiv weggeklickt wird (auch wenn
  // eine spätere Speicherung wieder klappt: welcher Stand tatsächlich auf dem Gerät
  // liegt, ist danach nicht mehr verlässlich).
  const [speicherFehler, setSpeicherFehler] = useState<string | null>(null);
  // Geräteübergreifender Abgleich (Ticket #488, lib/uebergabe/sync.ts): Stand für die
  // Anzeige und ein offener Konflikt (beide Geräte haben geändert).
  const [syncZustand, setSyncZustand] = useState<'aus' | 'gesichert' | 'laeuft' | 'ausstehend' | 'konflikt'>('aus');
  const [konflikt, setKonflikt] = useState(false);
  const revisionRef = useRef(0);
  const pushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pushLaeuftRef = useRef(false);

  const entwurfRef = useRef<LokalerEntwurf | null>(null);
  const speicherTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Riegel gegen die Wiederauferstehung des Entwurfs (Fund K3): nach erfolgreichem
  // Abschluss darf NICHTS mehr lokal geschrieben werden — weder ein noch laufender
  // Debounce-Timer noch der Flush beim Unmount (der beim Weg-Navigieren nach dem
  // Abschluss zwangsläufig feuert). Sonst legt der Flush den gerade gelöschten Entwurf
  // wieder an, und die Liste meldet nach JEDER Übergabe dauerhaft „1 Protokoll wartet
  // auf Upload" — womit die Warnung wertlos wird, die echte Datenverluste zeigen soll.
  const abgeschlossenRef = useRef(false);

  const meldeSpeicherFehler = useCallback((fehler: unknown) => {
    const text = fehler instanceof Error ? fehler.message : '';
    setSpeicherFehler(text || 'Unbekannter Fehler');
  }, []);

  // Jede Änderung entprellt (~500 ms) lokal sichern — Timer lebt im Ref, damit er
  // Renders übersteht und beim Unmount sofort (statt verzögert) geschrieben wird.
  const setEntwurf = useCallback<SetEntwurf>((updater) => {
    setEntwurfState(prev => {
      if (!prev) return prev;
      const geaendert = typeof updater === 'function' ? (updater as (v: LokalerEntwurf) => LokalerEntwurf)(prev) : updater;
      // Jede Eingabe ist bis zum nächsten erfolgreichen Abgleich „ungesichert" (Ticket #488)
      const next = geaendert === prev ? prev : { ...geaendert, ungesichert: true };
      if (next !== prev) revisionRef.current += 1;
      entwurfRef.current = next;
      if (speicherTimerRef.current) clearTimeout(speicherTimerRef.current);
      speicherTimerRef.current = setTimeout(() => {
        if (abgeschlossenRef.current) return;
        setSpeichertGerade(true);
        void speichereEntwurf(next).catch(meldeSpeicherFehler).finally(() => {
          setSpeichertGerade(false);
          planeAbgleichRef.current();
        });
      }, 500);
      return next;
    });
  }, [meldeSpeicherFehler]);

  // ── Abgleich mit dem Server (Ticket #488) ───────────────────────────────────
  // Lokal bleibt die Wahrheit für die Arbeit vor Ort (offline-fähig wie bisher); der
  // Server bekommt im Hintergrund einen Stand, den ein anderes Gerät öffnen kann.
  // Ergebnisse des Abgleichs laufen NICHT über setEntwurf — sie sind keine Eingabe und
  // dürfen den Entwurf nicht erneut als ungesichert markieren.
  const uebernimmAbgleich = useCallback((aendern: (e: LokalerEntwurf) => LokalerEntwurf) => {
    setEntwurfState(prev => {
      if (!prev || abgeschlossenRef.current) return prev;
      const next = aendern(prev);
      entwurfRef.current = next;
      void speichereEntwurf(next).catch(meldeSpeicherFehler);
      return next;
    });
  }, [meldeSpeicherFehler]);

  const schiebeJetztHoch = useCallback(async () => {
    const e = entwurfRef.current;
    if (!e || abgeschlossenRef.current || pushLaeuftRef.current || konfliktRef.current) return;
    if (!e.server_stand) { setSyncZustand('aus'); return; }
    if (!e.ungesichert) { setSyncZustand('gesichert'); return; }
    if (!navigator.onLine) { setSyncZustand('ausstehend'); return; }
    pushLaeuftRef.current = true;
    setSyncZustand('laeuft');
    const revision = revisionRef.current;
    try {
      const erg = await schiebeHoch(e, echteSyncDeps());
      if (erg.art === 'gesichert') {
        const weiterGeaendert = revisionRef.current !== revision;
        uebernimmAbgleich(prev => ({
          ...prev,
          server_stand: erg.stand,
          hochgeladen: [...new Set([...(prev.hochgeladen ?? []), ...erg.hochgeladen])],
          ungesichert: weiterGeaendert,
        }));
        setSyncZustand(weiterGeaendert ? 'ausstehend' : 'gesichert');
        if (weiterGeaendert) planeAbgleichRef.current();
      } else if (erg.art === 'konflikt') {
        uebernimmAbgleich(prev => ({ ...prev, hochgeladen: [...new Set([...(prev.hochgeladen ?? []), ...erg.hochgeladen])] }));
        konfliktRef.current = true;
        setKonflikt(true);
        setSyncZustand('konflikt');
      }
    } catch {
      // Netz weg o. Ä. — lokal ist alles da, der nächste Anlass versucht es erneut.
      setSyncZustand('ausstehend');
    } finally {
      pushLaeuftRef.current = false;
    }
  }, [uebernimmAbgleich]);

  const konfliktRef = useRef(false);
  const planeAbgleichRef = useRef<() => void>(() => {});
  planeAbgleichRef.current = () => {
    if (pushTimerRef.current) clearTimeout(pushTimerRef.current);
    pushTimerRef.current = setTimeout(() => { void schiebeJetztHoch(); }, 2500);
  };

  /** Konflikt auflösen: entweder den Stand des anderen Geräts holen oder den eigenen durchsetzen. */
  const loeseKonflikt = useCallback(async (wahl: 'server' | 'lokal') => {
    const e = entwurfRef.current;
    if (!e) return;
    try {
      if (wahl === 'server') {
        const r = await holeVomServer(e.id, echteSyncDeps(), e);
        if (!r) return;
        uebernimmAbgleich(() => r.entwurf);
        if (r.fehlend > 0) setFehlendeMedienHinweis([`${r.fehlend} Foto(s) bzw. Unterschrift(en) vom anderen Gerät ließen sich nicht laden`]);
        setSyncZustand('gesichert');
      } else {
        const stand = await ladeServerStand(e.id);
        if (!stand) return;
        uebernimmAbgleich(prev => ({ ...prev, server_stand: stand.updated_at, ungesichert: true }));
        konfliktRef.current = false;
        setKonflikt(false);
        await schiebeJetztHoch();
        return;
      }
      konfliktRef.current = false;
      setKonflikt(false);
    } catch {
      melde('Abgleich fehlgeschlagen — bitte Verbindung prüfen und erneut versuchen.', true);
    }
  }, [uebernimmAbgleich, schiebeJetztHoch]);

  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      setLadeFehler(false);
      try {
        const vorhanden = await ladeEntwurf(protokollId);
        if (abgebrochen) return;
        // Notanker aus localStorage hat Vorrang, falls neuer — deckt genau den Fall ab,
        // in dem ein harter Reload den letzten Tastendruck sonst verloren hätte. Kann
        // dabei Foto-/Unterschrift-Pfade ohne Blob entfernen (siehe lib/uebergabe/lokal.ts).
        const { entwurf: uebernommen, fehlendeMedien } = uebernehmeNotfallSchnappschuss(protokollId, vorhanden);
        if (fehlendeMedien.length > 0) setFehlendeMedienHinweis(fehlendeMedien);

        // Ticket #488: Wo steht der Server? Offline oder Fehler = null — dann läuft alles wie
        // vor dem Abgleich (lokal), und der nächste Anlass gleicht ab.
        const stand = navigator.onLine ? await ladeServerStand(protokollId).catch(() => null) : null;
        if (abgebrochen) return;
        const entscheidung = entscheideBeimOeffnen(uebernommen, stand);
        const vomServerMeldung = (n: number) => {
          if (n > 0) setFehlendeMedienHinweis([`${n} Foto(s) bzw. Unterschrift(en) vom anderen Gerät ließen sich nicht laden`]);
        };

        if (!uebernommen && entscheidung.art === 'vom_server') {
          // Im Büro vorbereitet, hier zum ersten Mal geöffnet: den Stand vom Server holen
          // statt neu anzulegen — genau der Ablauf aus Ticket #488.
          const r = await holeVomServer(protokollId, echteSyncDeps()).catch(() => null);
          if (abgebrochen) return;
          if (r) {
            entwurfRef.current = r.entwurf;
            setEntwurfState(r.entwurf);
            void speichereEntwurf(r.entwurf).catch(meldeSpeicherFehler);
            vomServerMeldung(r.fehlend);
            setSyncZustand('gesichert');
            return;
          }
        }

        if (uebernommen) {
          let e = uebernommen;
          if (entscheidung.art === 'server') {
            const r = await holeVomServer(protokollId, echteSyncDeps(), uebernommen).catch(() => null);
            if (abgebrochen) return;
            if (r) { e = r.entwurf; vomServerMeldung(r.fehlend); }
          } else if (entscheidung.art === 'lokal') {
            e = { ...e, server_stand: entscheidung.stand, ungesichert: entscheidung.hochschieben || !!e.ungesichert };
          } else if (entscheidung.art === 'konflikt') {
            konfliktRef.current = true;
            setKonflikt(true);
            setSyncZustand('konflikt');
          }
          entwurfRef.current = e;
          setEntwurfState(e);
          // Notanker bzw. Abgleich dauerhaft in IndexedDB übernehmen
          if (e !== vorhanden) void speichereEntwurf(e).catch(meldeSpeicherFehler);
          if (entscheidung.art === 'lokal' && entscheidung.hochschieben) planeAbgleichRef.current();
          else if (entscheidung.art === 'server' || (entscheidung.art === 'lokal' && entscheidung.stand)) setSyncZustand('gesichert');
        } else {
          // Kein lokaler Entwurf vorhanden — hier gilt: "ein bereits vorhandener lokaler
          // Entwurf gewinnt immer gegen den Bezug" ist bereits erfüllt (dieser Zweig läuft
          // NUR, wenn keiner existiert). Mit bezug (Neuanlage aus der Pillen-Vorauswahl)
          // werden die gewählten Kontakte geladen und per verteileRollen auf die drei
          // Parteien verteilt; schlägt das fehl (kein Netz), startet die Erfassung TROTZDEM
          // — nur eben ohne vorbelegte Parteinamen (Objekt/Einheit aus dem Bezug bleiben).
          let neu: LokalerEntwurf;
          // Ohne Vorauswahl (z. B. „Hier weiterbearbeiten" auf einem zweiten Gerät) den Bezug
          // aus der Kopfzeile nehmen — nie einen Entwurf ohne Objekt anlegen (Ticket #488).
          const bezugWirksam = bezug ?? (stand?.status === 'in_erfassung' ? stand.bezug ?? null : null);
          if (bezugWirksam) {
            try {
              const kontakte = await ladeKontakteFuerBezug(bezugWirksam);
              if (abgebrochen) return;
              const parteien = verteileRollen(bezugWirksam, kontakte, {
                name: userProfile?.full_name ?? '',
                email: userProfile?.email ?? null,
              });
              neu = erzeugeEntwurf(protokollId, userProfile, bezugWirksam, parteien);
            } catch {
              if (abgebrochen) return;
              neu = erzeugeEntwurf(protokollId, userProfile, bezugWirksam);
            }
          } else {
            neu = erzeugeEntwurf(protokollId, userProfile);
          }
          // Gibt es schon die Kopfzeile auf dem Server, kennt der neue Entwurf ihren Stand
          // und wird ab sofort mitgeschoben (Ticket #488).
          if (stand && stand.status === 'in_erfassung') neu = { ...neu, server_stand: stand.updated_at, ungesichert: true };
          entwurfRef.current = neu;
          setEntwurfState(neu);
          // sofort sichern, damit ein Reload direkt nach dem Anlegen nichts verliert
          void speichereEntwurf(neu).catch(meldeSpeicherFehler);
          if (neu.server_stand) planeAbgleichRef.current();
        }
      } catch {
        // Lokaler Speicher (IndexedDB) selbst nicht erreichbar/blockiert — ohne diesen
        // Fang bliebe die Seite für immer im Lade-Spinner hängen, ohne dass der Nutzer
        // erfährt, warum.
        if (!abgebrochen) setLadeFehler(true);
      }
    })();
    return () => { abgebrochen = true; };
    // userProfile UND bezug bewusst nicht in den Deps: beide werden nur für die
    // Neuanlage gebraucht (Zweig oben, der nur ohne lokalen Entwurf läuft) — ein neues
    // Objekt bei jedem Elternrender darf den Ladevorgang nicht wiederholen, sonst würde
    // ein bereits geladener/bearbeiteter Entwurf durch einen erneuten Lauf dieses Effekts
    // unnötig angefasst.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protokollId, ladeVersuch]);

  // Flush-Mechanismus: der 500-ms-Debounce in setEntwurf reicht nicht, wenn die Seite
  // *sofort* verschwindet (F5, Pull-to-Refresh, Tab schließen, App vom Betriebssystem
  // beendet) — das läuft am React-Unmount vorbei, der Browser reißt den Kontext ab.
  // pagehide und visibilitychange (state 'hidden') feuern dafür auf iOS/iPadOS
  // zuverlässiger als beforeunload. flush() liest immer den AKTUELLEN Stand aus den
  // Refs (nicht aus einer zum Zeitpunkt der Registrierung eingefrorenen Closure).
  //
  // Geprüft per echtem reload() ~150ms nach der letzten Eingabe: ein IndexedDB-put()
  // in diesem Handler kam NICHT mehr an (der Browser bricht die Transaktion vor dem
  // Commit ab) — deshalb zusätzlich der synchrone localStorage-Notanker, der das
  // zuverlässig übersteht (ebenfalls geprüft) und beim nächsten Laden in die echte
  // IndexedDB übernommen wird.
  useEffect(() => {
    const flush = () => {
      if (speicherTimerRef.current) {
        clearTimeout(speicherTimerRef.current);
        speicherTimerRef.current = null;
      }
      // Nach erfolgreichem Abschluss ist der Entwurf gelöscht — hier darf nichts mehr
      // zurückgeschrieben werden (Fund K3, s. abgeschlossenRef oben).
      if (abgeschlossenRef.current) return;
      const aktuell = entwurfRef.current;
      if (!aktuell) return;
      schreibeNotfallSchnappschuss(aktuell);
      // regulärer Pfad bleibt bestehen (z. B. Tab-Wechsel ohne Teardown)
      void speichereEntwurf(aktuell).catch(meldeSpeicherFehler);
    };
    const aufSichtbarkeit = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', aufSichtbarkeit);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', aufSichtbarkeit);
      flush(); // Unmount (z. B. Navigation innerhalb der SPA) verhält sich wie ein Verlassen der Seite.
    };
  }, [meldeSpeicherFehler]);

  useEffect(() => {
    const setzeOnline = () => { setOnline(true); planeAbgleichRef.current(); };
    const setzeOffline = () => setOnline(false);
    window.addEventListener('online', setzeOnline);
    window.addEventListener('offline', setzeOffline);
    return () => {
      window.removeEventListener('online', setzeOnline);
      window.removeEventListener('offline', setzeOffline);
    };
  }, []);

  // Objekt-Anschrift für die Kopfzeile — unabhängig von SchrittRahmen geladen, da
  // Schritt-Komponenten laut Vorgabe nur { entwurf, setEntwurf } bekommen.
  useEffect(() => {
    const objektId = entwurf?.protokoll.objekt_id;
    if (!objektId) { setObjektKopf(null); setObjektKopfFehler(false); return; }
    let abgebrochen = false;
    (async () => {
      setObjektKopfFehler(false);
      const { data, error } = await supabase.from('objekte').select('bezeichnung, strasse, hausnummer, plz, stadt').eq('id', objektId).maybeSingle();
      if (abgebrochen) return;
      if (error) { setObjektKopfFehler(true); return; }
      if (!data) return;
      setObjektKopf({ bezeichnung: data.bezeichnung, adresse: `${data.strasse} ${data.hausnummer}, ${data.plz} ${data.stadt}` });
    })();
    return () => { abgebrochen = true; };
  }, [entwurf?.protokoll.objekt_id, objektKopfVersuch]);

  const zurueck = () => {
    if (laeuft) return;
    if (schritt === 1) { onAbbrechen(); return; }
    setSchritt(s => (s - 1) as Schritt);
  };

  // Reihenfolge (siehe lib/uebergabe/abschluss.ts): Fotos/Unterschriften hochladen,
  // Protokoll upserten, Finalisierungs-Function aufrufen. Erst NACH erfolgreichem
  // Abschluss wird der lokale Entwurf gelöscht — schlägt irgendein Schritt in
  // schliesseAb fehl (z. B. Funkloch mitten im Upload), bleibt die komplette
  // Vor-Ort-Erfassung auf dem Gerät erhalten und kann später erneut gesendet werden.
  //
  // Wichtig: Erfolg hängt AUSSCHLIESSLICH an schliesseAb. Das anschließende Löschen
  // des lokalen Entwurfs ist reines Aufräumen — scheitert nur das (z. B. IndexedDB
  // gerade blockiert), ist das Protokoll auf dem Server dennoch fertig (PDF erzeugt,
  // Mails raus) und darf NIEMALS als „Abschluss fehlgeschlagen“ gemeldet werden, sonst
  // glaubt der Mitarbeiter vor Ort, nichts sei passiert, obwohl es das war. Deshalb
  // eigenes try/catch für loescheEntwurf, und onFertig() läuft in jedem Erfolgsfall.
  //
  // Die Sperre `if (laeuft || !entwurf) return;` steht bewusst als Erstes in der
  // Funktion selbst (nicht nur im deaktivierten Knopf) — Task 10 ruft schliesseAb
  // von einer zweiten Stelle aus auf („Jetzt senden“ in der Liste), und ein schneller
  // Doppel-Tap kann zwei Klick-Handler feuern, bevor React den Knopf deaktiviert.
  // Muster wie in pages/public/SignierenSeite.tsx:384-385.
  const abschliessen = async () => {
    if (laeuft || !entwurf) return;
    setLaeuft(true);
    try {
      // Fix-Runde 2 (kritischer Review-Fund): der Serverstatus wird VOR jedem Hochladen
      // geprüft. Ohne das konnte eine Kollegin vor Ort unbemerkt abschließen, obwohl das
      // Protokoll im Büro zwischenzeitlich abgebrochen (oder auf einem anderen Gerät
      // bereits abgeschlossen) wurde — schliesseAb hätte PDF/Mails ausgelöst und einen
      // Storno damit stillschweigend aufgehoben. holeServerStatus/istEndgueltig teilen
      // sich denselben Endgültigkeits-Begriff mit UebergabeListe.tsx (lib/uebergabe/
      // serverstatus.ts, lib/uebergabe/listenzeilen.ts).
      const serverStatus = await holeServerStatus(entwurf.id);
      if (istEndgueltig(serverStatus)) {
        // Fix-Runde 3 (kleiner Review-Fund): "abgebrochen" heißt zuverlässig, dass NICHTS
        // gesendet wurde (das Protokoll war beim Abbruch im Büro noch 'in_erfassung').
        // "abgeschlossen" kann dagegen genau der Wiederholungsfall sein, in dem ein
        // früherer Versuch server-seitig komplett durchlief (Upload, PDF, Mail) und NUR
        // die Erfolgsantwort verloren ging (Funkloch direkt danach) — "wurde NICHT
        // gesendet" wäre dort schlicht falsch. Die beiden Fälle brauchen daher
        // unterschiedliche, jeweils zutreffende Formulierungen.
        const text = serverStatus === 'abgebrochen'
          ? 'Dieses Protokoll wurde inzwischen abgebrochen. Deine Erfassung wurde NICHT gesendet.'
          : 'Dieses Protokoll ist bereits abgeschlossen — vermutlich lief ein früherer Versuch bereits vollständig durch und nur die Antwort ging verloren. Das PDF steht in der Übergabeprotokolle-Liste bereit.';
        // Nichts darf stillschweigend verschwinden: die Kollegin vor Ort entscheidet
        // selbst, ob der lokale Entwurf (Fotos, Zählerstände, Schlüssel, bereits
        // geleistete Unterschriften — bislang ausschließlich lokal) erhalten bleiben
        // soll oder gelöscht wird.
        const loeschen = await bestaetige({
          nachricht: `${text}\n\nLokalen Entwurf auf diesem Gerät jetzt löschen?`,
          label: 'Löschen', abbrechenLabel: 'Entwurf behalten',
        });
        if (loeschen) {
          // Dieselben zwei Schritte wie beim echten Erfolg unten (Fund K3): Riegel VOR
          // dem Löschen setzen — sonst schreibt der Debounce-Timer/Unmount-Flush beim
          // Verlassen der Seite den gerade gelöschten Entwurf sofort wieder zurück.
          abgeschlossenRef.current = true;
          entwurfRef.current = null;
          if (speicherTimerRef.current) {
            clearTimeout(speicherTimerRef.current);
            speicherTimerRef.current = null;
          }
          try {
            await loescheEntwurf(entwurf.id);
          } catch {
            meldeDanach('Lokaler Entwurf konnte nicht entfernt werden.', true);
          }
        }
        onAbbrechen();
        return;
      }

      // Ticket #488: Hat ein anderes Gerät seit dem letzten Abgleich etwas geändert, würde der
      // Abschluss es überschreiben — dann erst den Konflikt klären. Ein eigener, gerade
      // laufender Abgleich wird abgewartet, sonst hielte er sich selbst für fremd.
      for (let i = 0; i < 40 && pushLaeuftRef.current; i++) await new Promise((ok) => setTimeout(ok, 250));
      const aktuell = entwurfRef.current ?? entwurf;
      if (aktuell.server_stand) {
        const stand = await ladeServerStand(aktuell.id).catch(() => null);
        if (stand && stand.status === 'in_erfassung' && stand.updated_at !== aktuell.server_stand) {
          konfliktRef.current = true;
          setKonflikt(true);
          setSyncZustand('konflikt');
          melde('Dieses Protokoll wurde inzwischen auf einem anderen Gerät geändert. Bitte oben entscheiden, welcher Stand gilt, und dann erneut abschließen.', true);
          return;
        }
      }
      if (pushTimerRef.current) { clearTimeout(pushTimerRef.current); pushTimerRef.current = null; }

      const erg = await schliesseAb(aktuell, echteDeps());
      // Riegel VOR dem Löschen setzen (Fund K3): ab hier ist der Server fertig, jedes
      // weitere lokale Schreiben (Debounce-Timer, Unmount-Flush nach onFertig()) würde
      // den Entwurf wieder auferstehen lassen.
      abgeschlossenRef.current = true;
      entwurfRef.current = null;
      if (speicherTimerRef.current) {
        clearTimeout(speicherTimerRef.current);
        speicherTimerRef.current = null;
      }
      try {
        await loescheEntwurf(entwurf.id);
      } catch {
        meldeDanach('Protokoll ist abgeschlossen; der lokale Entwurf konnte nicht entfernt werden.', true);
      }
      onFertig();
      const meldungen = abschlussMeldungen(erg);
      if (meldungen) meldeDanach(`Protokoll abgeschlossen.\n\n${meldungen}`);
    } catch (err: any) {
      // Entwurf bleibt bewusst lokal — sonst wäre die Arbeit vor Ort verloren.
      melde('Abschluss fehlgeschlagen: ' + (err?.message || 'Unbekannter Fehler')
        + '\nDas Protokoll bleibt auf diesem Gerät gespeichert und kann später erneut gesendet werden.', true);
    } finally {
      setLaeuft(false);
    }
  };

  const weiter = () => {
    if (laeuft) return;
    if (schritt === LETZTER_SCHRITT) { void abschliessen(); return; }
    setSchritt(s => (s + 1) as Schritt);
  };

  const renderSchritt = () => {
    if (!entwurf) return null;
    switch (schritt) {
      case 1: return <SchrittRahmen entwurf={entwurf} setEntwurf={setEntwurf} bezugGesperrt={fuerHausmeister} />;
      case 2: return <SchrittRundgang entwurf={entwurf} setEntwurf={setEntwurf} />;
      case 3: return <SchrittZaehlerKontrolle entwurf={entwurf} setEntwurf={setEntwurf} />;
      case 4: return <SchrittSchluessel entwurf={entwurf} setEntwurf={setEntwurf} />;
      case 5: return <SchrittBemerkungen entwurf={entwurf} setEntwurf={setEntwurf} />;
      case 6: return <SchrittUnterschriften entwurf={entwurf} setEntwurf={setEntwurf} />;
      default: return null;
    }
  };

  // Abschluss-Sperre: dieselbe Regel (Übergeber + Übernehmer signiert, Zeuge
  // freiwillig) treibt sowohl den Sperrgrund-Text in SchrittUnterschriften als auch
  // den Fußzeilen-Knopf hier — daher der gemeinsame Import statt einer zweiten,
  // potenziell abweichenden Prüfung.
  const abschlussStatus = entwurf ? pruefeAbschlussBereit(entwurf.protokoll) : { bereit: false, grund: null as string | null };
  const rechtsGesperrt = laeuft || (schritt === LETZTER_SCHRITT && !abschlussStatus.bereit);

  const art = entwurf?.protokoll.art ?? null;
  const artFarbe = art ? ART_FARBE[art] : 'var(--acc)';
  const ArtIcon = art ? ART_ICON[art] : null;
  const netzText = !online
    ? (entwurf?.server_stand ? 'Offline — lokal gesichert, wird nachgeschoben' : 'Offline — lokal gesichert')
    : syncZustand === 'gesichert' ? 'Auf allen Geräten gesichert'
    : syncZustand === 'laeuft' ? 'Wird abgeglichen …'
    : syncZustand === 'ausstehend' ? 'Wird gleich abgeglichen'
    : syncZustand === 'konflikt' ? 'Zwei Fassungen'
    : 'Online';
  const netzFarbe = !online || syncZustand === 'konflikt' ? 'var(--warn)' : 'var(--ok)';
  const letzter = schritt === LETZTER_SCHRITT;
  const weiterTip = laeuft ? 'Wird hochgeladen …'
    : letzter && !abschlussStatus.bereit ? abschlussStatus.grund ?? undefined
    : letzter ? 'Übergabe abschließen – PDF erzeugen, versenden und ablegen'
    : `Weiter zu ${SCHRITT_LABEL[(schritt + 1) as Schritt]}`;
  const knopf = { height: mobil ? 48 : 46, borderRadius: 15, border: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 7 } as const;

  return (
    <>
      <GlasTooltipSchicht />
      {/* Kopf: Titel, Netzanzeige und Fortschritt in einem Widget */}
      {/* Abstand oben: der Chat-Knopf der Seite sitzt rechts oben (ImmobilienInhalt) und darf den Kopf nicht überdecken */}
      <section aria-label="Übergabe" className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: mobil ? 16 : 20, gap: 16, marginTop: mobil || fuerHausmeister ? 0 : 36 }}>
        <div className="flex flex-wrap items-start" style={{ gap: 14 }}>
          {ArtIcon && (
            <span aria-hidden className="inline-flex shrink-0 items-center justify-center"
              style={{ width: 52, height: 52, borderRadius: 16, background: artFarbe, color: '#fff', boxShadow: `0 10px 22px -10px ${artFarbe}` }}>
              <ArtIcon size={26} weight="fill" />
            </span>
          )}
          <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 4, flexBasis: 220 }}>
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
              Übergabeprotokoll{art ? ` · ${ART_LABEL[art]}` : ''}
            </span>
            <h1 className="m-0" style={{ fontSize: mobil ? 22 : 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.15, overflowWrap: 'anywhere' }}>
              {objektKopf ? `${objektKopf.bezeichnung} · ${objektKopf.adresse}` : 'Neue Übergabe'}
            </h1>
          </div>
          <div className="flex shrink-0 flex-wrap items-center" style={{ gap: 8 }}>
            {speichertGerade && (
              <span className="inline-flex items-center" style={{ gap: 5, fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>
                <CircleNotch size={13} weight="bold" className="animate-spin" /> Speichert…
              </span>
            )}
            <span data-tip={online ? 'Online – Eingaben werden zusätzlich lokal gesichert' : 'Offline – Alles bleibt auf diesem Gerät, bis wieder Verbindung besteht'}
              className="inline-flex items-center" style={{ height: 32, padding: '0 12px', gap: 6, borderRadius: 16, fontSize: 12, fontWeight: 800,
                background: `color-mix(in srgb, ${netzFarbe} 14%, transparent)`, color: netzFarbe }}>
              {online ? <WifiHigh size={14} weight="bold" /> : <WifiSlash size={14} weight="bold" />}{netzText}
            </span>
          </div>
        </div>

        {/* Ticket #488: beide Geräte haben seit dem letzten gemeinsamen Stand geändert. Nichts
            wird stillschweigend überschrieben — hier entscheidet der Mensch. */}
        {konflikt && (
          <div className="flex flex-col" style={{ gap: 10, padding: '14px 16px', borderRadius: 18, background: 'var(--warn-soft)' }}>
            <span className="flex items-start" style={{ gap: 8, fontSize: 14, fontWeight: 800, color: 'var(--warn)' }}>
              <WarningCircle size={18} weight="fill" className="shrink-0" />Dieses Protokoll wurde auch auf einem anderen Gerät bearbeitet.
            </span>
            <span style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--ink2)' }}>
              Auf diesem Gerät gibt es Änderungen, die noch nicht abgeglichen sind — und auf dem Server einen neueren Stand
              vom anderen Gerät. Welcher soll gelten? Der andere Stand geht dabei verloren.
            </span>
            <div className="flex flex-wrap" style={{ gap: 8 }}>
              <button type="button" onClick={() => void loeseKonflikt('server')}
                style={{ ...knopf, height: 44, padding: '0 16px', cursor: 'pointer', background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
                Stand vom anderen Gerät übernehmen
              </button>
              <button type="button" onClick={async () => {
                if (await bestaetige({ nachricht: 'Den Stand dieses Geräts behalten?\n\nDie Änderungen vom anderen Gerät werden dann überschrieben.', label: 'Meinen Stand behalten' })) void loeseKonflikt('lokal');
              }}
                style={{ ...knopf, height: 44, padding: '0 16px', cursor: 'pointer', background: 'var(--bg)', color: 'var(--warn)' }}>
                Meinen Stand behalten
              </button>
            </div>
          </div>
        )}

        {objektKopfFehler && (
          <LadeHinweis text="Objekt-Anschrift konnte nicht geladen werden — Verbindung prüfen." aufErneutVersuchen={() => setObjektKopfVersuch(v => v + 1)} />
        )}

        <BalkenSchritte labels={SCHRITTE.map(n => SCHRITT_LABEL[n])} aktuell={schritt} mitLabel={viewport.width >= 760}
          onWahl={fuerHausmeister && !laeuft ? (n) => setSchritt(n as Schritt) : undefined} />
      </section>

      {/* Lokales Speichern fehlgeschlagen (Fund W2) — ab hier ist die Vor-Ort-Erfassung nicht mehr gesichert. */}
      {speicherFehler !== null && (
        <HinweisWidget ton="bad" titel="Lokales Speichern fehlgeschlagen — bitte Übergabe zügig abschließen." onSchliessen={() => setSpeicherFehler(null)}>
          Eingaben werden auf diesem Gerät nicht mehr zuverlässig gesichert{speicherFehler ? ` (${speicherFehler})` : ''}. Ein Neuladen der Seite kann Erfasstes kosten.
        </HinweisWidget>
      )}

      {/* Fehlende Medien nach Notanker-Übernahme — bleibt stehen, bis aktiv weggeklickt. */}
      {fehlendeMedienHinweis && fehlendeMedienHinweis.length > 0 && (
        <HinweisWidget ton="warn" onSchliessen={() => setFehlendeMedienHinweis(null)}
          titel={<>Nach dem Neustart {fehlendeMedienHinweis.length === 1 ? 'fehlt 1 Beweismittel' : `fehlen ${fehlendeMedienHinweis.length} Beweismittel`}
            {' '}({fehlendeMedienHinweis.join(', ')}). Bitte erneut aufnehmen bzw. unterschreiben.</>} />
      )}

      <h2 className="m-0" style={{ fontSize: mobil ? 19 : 22, fontWeight: 800, letterSpacing: '-0.02em' }}>
        Schritt {schritt} von {SCHRITTE.length} · {SCHRITT_LABEL[schritt]}
      </h2>

      {/* Inhalt — gleitet je Schritt ein */}
      <div key={schritt} className="immo-side-in flex min-w-0 flex-col" style={{ gap: 14 }}>
        {!entwurf ? (
          ladeFehler ? (
            <LadeHinweis text="Entwurf konnte nicht geladen werden — Verbindung/lokalen Speicher prüfen." aufErneutVersuchen={() => setLadeVersuch(v => v + 1)} />
          ) : (
            <div className="flex items-center justify-center" style={{ padding: '80px 0', color: 'var(--acc)' }}>
              <CircleNotch size={32} weight="bold" className="animate-spin" />
            </div>
          )
        ) : renderSchritt()}
      </div>

      {/* Untere Leiste — sticky, große Touch-Ziele; Sperrgrund/Upload-Status als sichtbarer Text, nicht nur ausgegraut. */}
      <div className="immo-glas sticky flex flex-wrap items-center justify-between"
        style={{ bottom: mobil ? 10 : 16, zIndex: 20, borderRadius: 22, padding: '10px 10px 10px 16px', gap: 10 }}>
        <span className="flex min-w-0 items-center" style={{ gap: 7, fontSize: 13, fontWeight: 700, flex: '1 1 160px',
          color: !laeuft && letzter && !abschlussStatus.bereit ? 'var(--warn)' : 'var(--ink2)' }}>
          {laeuft ? <><CircleNotch size={14} weight="bold" className="animate-spin shrink-0" /> Wird hochgeladen …</>
            : letzter && !abschlussStatus.bereit ? abschlussStatus.grund
            : `Schritt ${schritt} von ${SCHRITTE.length} · ${SCHRITT_LABEL[schritt]}`}
        </span>
        <span className="flex shrink-0" style={{ gap: 8, marginLeft: 'auto' }}>
          <button type="button" onClick={zurueck} disabled={laeuft}
            style={{ ...knopf, padding: '0 16px', cursor: laeuft ? 'default' : 'pointer', background: 'var(--fill2)', color: 'var(--ink)', opacity: laeuft ? 0.45 : 1 }}>
            {schritt === 1 ? <X size={15} weight="bold" /> : <CaretLeft size={15} weight="bold" />}{schritt === 1 ? 'Abbrechen' : 'Zurück'}
          </button>
          <button type="button" onClick={weiter} disabled={rechtsGesperrt} data-tip={weiterTip}
            style={{ ...knopf, padding: '0 20px', cursor: rechtsGesperrt ? 'default' : 'pointer', opacity: rechtsGesperrt ? 0.45 : 1, color: letzter ? '#fff' : 'var(--acc-ink)',
              background: letzter ? 'linear-gradient(160deg,#4cd964,#28a745)' : 'var(--acc-fill)', boxShadow: rechtsGesperrt ? 'none' : letzter ? '0 10px 22px -10px #28a745' : 'var(--acc-sh)' }}>
            {letzter ? <><Check size={15} weight="bold" />Übergabe abschließen</> : <>Weiter<CaretRight size={15} weight="bold" /></>}
          </button>
        </span>
      </div>
      {dialoge}
    </>
  );
};

export default UebergabeAssistent;
